import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'

import { useGetStoresQuery } from '@/store/api/storesApi'
import { iconFromString, colorFromString } from '@/components/stores/storeMeta'
import LibraryPickerDialog from './LibraryPickerDialog'

/**
 * Botón permanente del Sidebar para seleccionar la biblioteca activa.
 * Muestra el avatar/nombre de la biblioteca seleccionada y abre un Dialog
 * con buscador para cambiarla.
 */
const LibrarySelector = ({ colors }) => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    const selectedStoreId = useSelector((s) => s.auth.settings?.selectedStoreId)
    const { data } = useGetStoresQuery({ limit: 1000 })

    const store = data?.items?.find((s) => s.tid === selectedStoreId) || null

    const Icon = store ? iconFromString(store.name) : LibraryBooksIcon
    const bg = store ? colorFromString(store.name) : 'grey.700'

    return (
        <>
            <Box sx={{ px: 1.5, pb: 1 }}>
                <ButtonBase
                    onClick={() => setOpen(true)}
                    sx={{
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: 1.5,
                        p: 1,
                        gap: 1,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        color: colors?.text || 'inherit',
                        transition: 'background-color 120ms ease',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                    }}>
                    <Avatar sx={{ bgcolor: bg, width: 36, height: 36 }}>
                        <Icon fontSize='small' />
                    </Avatar>
                    <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0}>
                        <Typography
                            variant='caption'
                            sx={{
                                color: colors?.text || 'text.secondary',
                                opacity: 0.6,
                                lineHeight: 1.1
                            }}>
                            {t('Library')}
                        </Typography>
                        <Typography
                            variant='body2'
                            noWrap
                            sx={{
                                color: colors?.activeText || 'text.primary',
                                fontWeight: 600
                            }}>
                            {store ? store.name : t('No Library Selected')}
                        </Typography>
                    </Stack>
                    <UnfoldMoreIcon
                        fontSize='small'
                        sx={{ color: colors?.text || 'inherit', opacity: 0.6 }}
                    />
                </ButtonBase>
            </Box>

            <LibraryPickerDialog open={open} onClose={() => setOpen(false)} />
        </>
    )
}

export default LibrarySelector
